import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Clock, MapPin, Check, X, ArrowLeft, MessageCircle, Calendar as CalendarIcon,
  Utensils, Hotel, Car, Route, Users, Activity, Download, Heart, Loader2, Star,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatErrorDetail, API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageLayout from "@/components/layout/PageLayout";
import SketchMap from "@/components/SketchMap";
import SocialShare from "@/components/SocialShare";
import BookingWidget from "@/components/BookingWidget";
import Seo from "@/components/Seo";
import { breadcrumbSchema } from "@/components/JsonLd";
import PriceComparison from "@/components/PriceComparison";
import TourCard from "@/components/TourCard";
import { Price } from "@/context/CurrencyContext";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";

const COUNTRY_FLAGS = {
  India: "🇮🇳", Nepal: "🇳🇵", Bhutan: "🇧🇹", "Sri Lanka": "🇱🇰",
  Maldives: "🇲🇻", Thailand: "🇹🇭", UAE: "🇦🇪",
};

export default function TourDetail() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [tour, setTour] = useState(null);
  const [testimonials, setTestimonials] = useState([]);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [inWishlist, setInWishlist] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [t, r] = await Promise.all([
          api.get(`/tours/${slug}`),
          api.get("/testimonials"),
        ]);
        setTour(t.data);
        setTestimonials(r.data);
      } catch (_) {
        setTour(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  // Fetch related tours from the first theme this tour is tagged with.
  useEffect(() => {
    const themes = tour?.themes || [];
    if (themes.length === 0) {
      setRelated([]);
      return;
    }
    let alive = true;
    api
      .get(`/tours`, { params: { theme: themes[0] } })
      .then((res) => {
        if (!alive) return;
        // Exclude the current tour and cap at 3.
        const filtered = (res.data || []).filter((t) => t.slug !== slug).slice(0, 3);
        setRelated(filtered);
      })
      .catch(() => alive && setRelated([]));
    return () => {
      alive = false;
    };
  }, [tour, slug]);

  useEffect(() => {
    if (user?.wishlist?.includes(slug)) setInWishlist(true);
    else setInWishlist(false);
  }, [user, slug]);

  const toggleWishlist = async () => {
    if (!user) {
      toast.info("Sign in to save tours to your wishlist.");
      return;
    }
    try {
      if (inWishlist) {
        await api.delete(`/wishlist/${slug}`);
        setInWishlist(false);
        toast.success("Removed from wishlist");
      } else {
        await api.post("/wishlist", { tour_slug: slug });
        setInWishlist(true);
        toast.success("Saved to wishlist");
      }
    } catch (e) {
      toast.error(formatErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  if (loading) {
    return (
      <PageLayout>
        <div className="min-h-screen flex items-center justify-center text-foreground/60">Loading journey…</div>
      </PageLayout>
    );
  }

  if (!tour) {
    return (
      <PageLayout>
        <div className="min-h-screen flex flex-col items-center justify-center text-center px-6">
          <h1 className="font-serif text-5xl">Tour not found.</h1>
          <Link to="/tours" className="mt-6 btn-primary">Browse all tours</Link>
        </div>
      </PageLayout>
    );
  }

  const flag = COUNTRY_FLAGS[tour.country] || "🌏";
  const tourTestimonials = testimonials.filter(
    (t) => t.tour_name?.toLowerCase().includes(tour.title.toLowerCase().split(" ")[0])
  );

  return (
    <PageLayout>
      <Seo
        title={tour.title}
        description={tour.summary || tour.description?.slice(0, 160)}
        image={tour.image_url}
        type="article"
        jsonLd={[
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Tours", path: "/tours" },
            { name: tour.title, path: `/tours/${tour.slug}` },
          ]),
          {
            "@context": "https://schema.org",
            "@type": "TouristTrip",
            "name": tour.title,
            "description": tour.summary || (tour.description || "").slice(0, 600),
            "image": [tour.image_url, ...((tour.gallery || []).slice(0, 5))],
            "duration": `P${tour.days || 1}D`,
            "touristType": tour.category,
            "itinerary": (tour.itinerary || []).map((d, i) => ({
              "@type": "Place",
              "name": d.title || `Day ${d.day || i + 1}`,
              "description": d.description || d.details || "",
            })),
            "offers": {
              "@type": "Offer",
              "price": tour.price_usd,
              "priceCurrency": "USD",
              "availability": "https://schema.org/InStock",
              "url": typeof window !== "undefined" ? window.location.href : "",
            },
            "provider": {
              "@type": "TravelAgency",
              "name": "Tour Trips India Holidays",
              "url": "https://ttiholidays.com",
              "telephone": "+91-70177-98524",
              "email": "info@ttiholidays.com",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "P-10, Tajnagri Phase 1, Tajganj",
                "addressLocality": "Agra",
                "postalCode": "282001",
                "addressRegion": "Uttar Pradesh",
                "addressCountry": "IN",
              },
            },
            ...(testimonials.length > 0 ? {
              "aggregateRating": {
                "@type": "AggregateRating",
                "ratingValue": (testimonials.reduce((s, t) => s + (t.rating || 5), 0) / testimonials.length).toFixed(1),
                "reviewCount": testimonials.length,
                "bestRating": 5,
                "worstRating": 1,
              },
            } : {}),
          },
        ]}
      />
      {/* HERO */}
      <section className="relative h-[80vh] min-h-[600px] flex items-end" data-testid="tour-detail-hero">
        <div className="absolute inset-0">
          <img src={tour.image_url} alt={tour.title} className="w-full h-full object-cover animate-slow-zoom" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/30 to-black/85" />
        </div>
        <div className="container-tti relative z-10 pb-20 pt-32">
          <Link to="/tours" className="inline-flex items-center gap-2 text-white/80 text-sm hover:text-marigold mb-8" data-testid="back-to-tours">
            <ArrowLeft className="w-4 h-4" /> All Tours
          </Link>
          <div className="flex items-center gap-3 mb-5 flex-wrap">
            <span className="px-3 py-1 bg-saffron text-white text-[10px] uppercase tracking-[0.25em] font-semibold rounded-sm">
              {tour.category.replace(/-/g, " ")}
            </span>
            <span className="text-xl">{flag}</span>
            <span className="text-white/80 text-sm">{tour.country}{tour.region ? ` · ${tour.region}` : ""}</span>
            <span className="text-white/80 text-sm">· {tour.duration}</span>
          </div>
          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-white tracking-tighter leading-[0.95] max-w-4xl">
            {tour.title}
          </h1>
          <p className="text-white/85 text-lg max-w-2xl mt-6 font-light leading-relaxed">{tour.summary}</p>
          <div className="flex flex-wrap items-center gap-5 mt-8 text-sm text-white/85">
            {tour.locations?.length > 0 && (
              <span className="flex items-center gap-2"><MapPin className="w-4 h-4 text-marigold" />{tour.locations.slice(0, 5).join(" · ")}</span>
            )}
            <span className="flex items-center gap-2"><Users className="w-4 h-4 text-marigold" />Max {tour.max_group_size}</span>
            <span className="flex items-center gap-2"><Activity className="w-4 h-4 text-marigold" />{tour.difficulty_level}</span>
          </div>
        </div>
      </section>

      {/* BOOKING STRIP */}
      <section className="bg-deepred text-white sticky top-24 z-30 shadow-md" data-testid="booking-strip">
        <div className="container-tti py-5 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-marigold">Price starts from</div>
            <div className="font-serif text-3xl">
              <Price usd={tour.price_usd} /> <span className="text-sm text-white/70 ml-1 font-sans">/ person · twin share</span>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={toggleWishlist}
              data-testid="wishlist-toggle"
              className="inline-flex items-center px-4 py-3 rounded-sm border border-white/40 text-white text-sm uppercase tracking-wider font-medium hover:bg-white hover:text-deepred transition-all"
            >
              <Heart className={`w-4 h-4 mr-2 ${inWishlist ? "fill-marigold text-marigold" : ""}`} /> {inWishlist ? "Saved" : "Wishlist"}
            </button>
            <a href={`${API_BASE}/tours/${slug}/itinerary.pdf`} download className="inline-flex items-center px-4 py-3 rounded-sm border border-white/40 text-white text-sm uppercase tracking-wider font-medium hover:bg-white hover:text-deepred transition-all" data-testid="download-pdf">
              <Download className="w-4 h-4 mr-2" /> PDF
            </a>
            <a href="https://wa.me/917017798524" target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-4 py-3 rounded-sm border border-white/40 text-white text-sm uppercase tracking-wider font-medium hover:bg-white hover:text-deepred transition-all" data-testid="tour-whatsapp">
              <MessageCircle className="w-4 h-4 mr-2" /> WhatsApp
            </a>
            <button
              onClick={() => setActiveTab("book")}
              data-testid="scroll-to-book"
              className="inline-flex items-center px-6 py-3 rounded-sm bg-marigold text-deepred-700 font-semibold text-sm uppercase tracking-wider hover:bg-white transition-all"
            >
              <CalendarIcon className="w-4 h-4 mr-2" /> Book Now
            </button>
          </div>
        </div>
      </section>

      {/* TABS CONTENT */}
      <section className="py-16" data-testid="tour-tabs-section">
        <div className="container-tti">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="bg-cream-100 border border-cream-200 rounded-sm mb-10 h-auto overflow-x-auto no-scrollbar w-full md:w-auto" data-testid="tour-tabs">
              <TabsTrigger value="overview" data-testid="tab-overview" className="px-5 py-3 text-xs uppercase tracking-[0.15em] font-medium">Overview & Highlights</TabsTrigger>
              <TabsTrigger value="itinerary" data-testid="tab-itinerary" className="px-5 py-3 text-xs uppercase tracking-[0.15em] font-medium">Day-by-Day</TabsTrigger>
              <TabsTrigger value="inclusions" data-testid="tab-inclusions" className="px-5 py-3 text-xs uppercase tracking-[0.15em] font-medium">Inclusions</TabsTrigger>
              <TabsTrigger value="reviews" data-testid="tab-reviews" className="px-5 py-3 text-xs uppercase tracking-[0.15em] font-medium">Reviews</TabsTrigger>
              <TabsTrigger value="book" data-testid="tab-book" className="px-5 py-3 text-xs uppercase tracking-[0.15em] font-medium">Book / Inquire</TabsTrigger>
            </TabsList>

            <div className="grid lg:grid-cols-12 gap-12">
              <div className="lg:col-span-8">
                <TabsContent value="overview" className="space-y-12 mt-0">
                  <div>
                    <div className="eyebrow mb-3">Overview</div>
                    <h2 className="font-serif text-3xl md:text-4xl tracking-tight mb-5">About this journey</h2>
                    <p className="text-foreground/80 text-[17px] leading-relaxed font-light">{tour.description}</p>
                  </div>
                  {tour.highlights?.length > 0 && (
                    <div>
                      <div className="eyebrow mb-3">Experience</div>
                      <h2 className="font-serif text-3xl md:text-4xl tracking-tight mb-6">Tour highlights</h2>
                      <div className="grid sm:grid-cols-2 gap-3">
                        {tour.highlights.map((h, i) => (
                          <div key={i} className="flex items-start gap-3 bg-cream-100 p-4 rounded-sm">
                            <div className="w-6 h-6 rounded-sm bg-saffron text-white flex items-center justify-center flex-shrink-0 mt-0.5">
                              <Check className="w-3.5 h-3.5" />
                            </div>
                            <span className="text-[15px] font-light text-foreground/85 leading-snug">{h}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {tour.gallery?.length > 1 && (
                    <div>
                      <div className="eyebrow mb-3">Gallery</div>
                      <h2 className="font-serif text-3xl md:text-4xl tracking-tight mb-6">A glimpse</h2>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {tour.gallery.map((src, idx) => (
                          <div key={idx} className="aspect-square overflow-hidden rounded-sm bg-cream-200">
                            <img src={src} alt={`${tour.title} ${idx + 1}`} loading="lazy" className="w-full h-full object-cover hover:scale-110 transition-transform duration-700" />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="itinerary" className="space-y-12 mt-0">
                  <div>
                    <div className="eyebrow mb-3">Day by day</div>
                    <h2 className="font-serif text-3xl md:text-4xl tracking-tight mb-8">Itinerary</h2>
                    <div className="space-y-px bg-cream-200 border border-cream-200">
                      {tour.itinerary?.map((it) => (
                        <div key={it.day} className="bg-white p-6 md:p-8 flex gap-6">
                          <div className="flex-shrink-0">
                            <div className="text-[10px] uppercase tracking-[0.25em] text-saffron">Day</div>
                            <div className="font-serif text-4xl text-deepred leading-none">{it.day}</div>
                          </div>
                          <div className="flex-1">
                            <h3 className="font-serif text-2xl mb-2">{it.title}</h3>
                            <p className="text-sm text-foreground/75 font-light leading-relaxed mb-4">{it.details}</p>
                            <div className="grid sm:grid-cols-2 gap-3 text-xs mt-4">
                              {it.accommodation && <div className="flex gap-2 items-start"><Hotel className="w-3.5 h-3.5 text-saffron flex-shrink-0 mt-0.5" /><div><span className="text-foreground/50">Stay: </span><span className="text-foreground/85">{it.accommodation}</span></div></div>}
                              {it.meals && <div className="flex gap-2 items-start"><Utensils className="w-3.5 h-3.5 text-saffron flex-shrink-0 mt-0.5" /><div><span className="text-foreground/50">Meals: </span><span className="text-foreground/85">{it.meals}</span></div></div>}
                              {it.transfer && <div className="flex gap-2 items-start"><Car className="w-3.5 h-3.5 text-saffron flex-shrink-0 mt-0.5" /><div><span className="text-foreground/50">Transfer: </span><span className="text-foreground/85">{it.transfer}</span></div></div>}
                              {it.distance && <div className="flex gap-2 items-start"><Route className="w-3.5 h-3.5 text-saffron flex-shrink-0 mt-0.5" /><div><span className="text-foreground/50">Distance: </span><span className="text-foreground/85">{it.distance}</span></div></div>}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {tour.sketch_map_coords?.length > 0 && (
                    <div>
                      <div className="eyebrow mb-3">Route</div>
                      <h2 className="font-serif text-3xl md:text-4xl tracking-tight mb-6">Sketch map of your journey</h2>
                      <SketchMap coords={tour.sketch_map_coords} height={420} />
                      <div className="mt-3 text-xs text-foreground/55 text-center">
                        Dashed line shows the route through {tour.sketch_map_coords.length} stops
                      </div>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="inclusions" className="space-y-10 mt-0">
                  <div className="grid md:grid-cols-2 gap-8">
                    {tour.inclusions?.length > 0 && (
                      <div>
                        <h3 className="font-serif text-2xl mb-5 text-deepred">What&rsquo;s included</h3>
                        <ul className="space-y-3">
                          {tour.inclusions.map((i, idx) => (
                            <li key={idx} className="flex gap-3 text-[15px] font-light">
                              <Check className="w-5 h-5 text-saffron flex-shrink-0 mt-0.5" />
                              <span>{i}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {tour.exclusions?.length > 0 && (
                      <div>
                        <h3 className="font-serif text-2xl mb-5 text-foreground/60">Not included</h3>
                        <ul className="space-y-3">
                          {tour.exclusions.map((i, idx) => (
                            <li key={idx} className="flex gap-3 text-[15px] font-light text-foreground/70">
                              <X className="w-5 h-5 text-foreground/40 flex-shrink-0 mt-0.5" />
                              <span>{i}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="reviews" className="mt-0">
                  <div className="eyebrow mb-3">Reviews</div>
                  <h2 className="font-serif text-3xl md:text-4xl tracking-tight mb-8">What travelers said</h2>
                  {tourTestimonials.length === 0 ? (
                    <div className="text-foreground/60 font-light">No reviews yet for this exact tour — see all <Link to="/testimonials" className="text-saffron">traveler stories →</Link></div>
                  ) : (
                    <div className="grid sm:grid-cols-2 gap-5">
                      {tourTestimonials.map((t) => (
                        <div key={t.id} className="bg-cream-100 border border-cream-200 rounded-sm p-6">
                          <div className="flex gap-0.5 mb-3">{[...Array(t.rating)].map((_, i) => <Star key={i} className="w-3.5 h-3.5 fill-saffron text-saffron" />)}</div>
                          <p className="text-sm font-light text-foreground/85 leading-relaxed mb-4">{t.text}</p>
                          <div className="flex items-center gap-3 pt-3 border-t border-cream-200">
                            <img src={t.avatar_url} alt={t.name} className="w-9 h-9 rounded-full" />
                            <div>
                              <div className="text-sm font-medium">{t.name}</div>
                              <div className="text-[10px] uppercase tracking-[0.2em] text-foreground/60">{t.location}</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="book" className="mt-0">
                  <BookingWidget tour={tour} />
                </TabsContent>
              </div>

              {/* SIDEBAR */}
              <aside className="lg:col-span-4">
                <div className="lg:sticky lg:top-48 space-y-6">
                  <div className="bg-cream-100 border border-cream-200 rounded-sm p-7 space-y-5">
                    <div>
                      <div className="eyebrow mb-2">Trip facts</div>
                      <div className="text-sm space-y-2.5 text-foreground/80">
                        <div className="flex justify-between"><span className="text-foreground/55">Duration</span><span className="font-medium">{tour.duration}</span></div>
                        <div className="flex justify-between"><span className="text-foreground/55">Country</span><span className="font-medium">{flag} {tour.country}</span></div>
                        <div className="flex justify-between"><span className="text-foreground/55">Region</span><span className="font-medium">{tour.region || "—"}</span></div>
                        <div className="flex justify-between"><span className="text-foreground/55">Difficulty</span><span className="font-medium">{tour.difficulty_level}</span></div>
                        <div className="flex justify-between"><span className="text-foreground/55">Group size</span><span className="font-medium">Max {tour.max_group_size}</span></div>
                        <div className="flex justify-between pt-2 border-t border-cream-200 text-base"><span className="text-foreground/55">Price starts from</span><span className="font-serif text-2xl text-deepred"><Price usd={tour.price_usd} /></span></div>
                      </div>
                    </div>
                    <button onClick={() => setActiveTab("book")} className="btn-primary w-full" data-testid="sidebar-book-now">Book Now</button>
                    <a href="https://wa.me/917017798524" target="_blank" rel="noopener noreferrer" className="btn-outline w-full" data-testid="sidebar-whatsapp"><MessageCircle className="w-4 h-4 mr-2" /> WhatsApp</a>
                  </div>

                  {/* PRICE COMPARISON — direct vs OTA */}
                  <PriceComparison tour={tour} />

                  <div className="bg-white border border-cream-200 rounded-sm p-7">
                    <div className="eyebrow mb-3">Share this journey</div>
                    <SocialShare title={`${tour.title} — TTI Holidays`} description={tour.summary} />
                  </div>
                </div>
              </aside>
            </div>
          </Tabs>
        </div>
      </section>

      {/* Related tours — same theme */}
      {related.length > 0 && (
        <section className="py-20 md:py-24 bg-cream" data-testid="related-tours">
          <div className="container-tti">
            <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
              <div>
                <div className="eyebrow mb-3">You may also like</div>
                <h2 className="font-serif text-3xl md:text-4xl leading-tight">
                  More {tour.themes?.[0] === "yoga" ? "yoga & wellness" : tour.themes?.[0] === "wildlife" ? "wildlife" : tour.themes?.[0] === "festivals" ? "festival" : ""} journeys
                </h2>
              </div>
              {tour.themes?.[0] && (
                <Link
                  to={`/themes/${tour.themes[0]}`}
                  className="text-sm uppercase tracking-[0.25em] text-deepred hover:text-saffron transition-colors inline-flex items-center gap-2"
                  data-testid="related-tours-theme-link"
                >
                  See all →
                </Link>
              )}
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {related.map((t) => (
                <TourCard key={t.id || t.slug} tour={t} />
              ))}
            </div>
          </div>
        </section>
      )}
    </PageLayout>
  );
}
